import { spawn } from 'child_process';
import { rgPath } from '@vscode/ripgrep';

export type ExecResult = { stdout: string; stderr: string; exitCode: number };

// Cache the working ripgrep path to avoid repeated fallback checks
let resolvedRgPath: string | null = null;
let rgPathChecked = false;

function spawnRg(rgBinary: string, args: string[], opts?: { cwd?: string; env?: NodeJS.ProcessEnv }): Promise<ExecResult> {
  return new Promise((resolve) => {
    const child = spawn(rgBinary, args, {
      cwd: opts?.cwd,
      env: { ...process.env, ...(opts?.env || {}) },
      stdio: ['ignore', 'pipe', 'pipe'],
    });
    let stdout = '';
    let stderr = '';
    child.stdout.on('data', (d) => (stdout += d.toString()));
    child.stderr.on('data', (d) => (stderr += d.toString()));
    child.on('close', (code) => {
      resolve({ stdout, stderr, exitCode: typeof code === 'number' ? code : -1 });
    });
    child.on('error', () => {
      resolve({ stdout: '', stderr: 'Failed to spawn ripgrep.', exitCode: -1 });
    });
  });
}

// Check if a result indicates a binary compatibility failure (not a normal rg error)
function isBinaryFailure(result: ExecResult): boolean {
  // Spawn error
  if (result.exitCode === -1) return true;
  // jemalloc page size issue (common on ARM64 with non-standard page sizes)
  if (result.stderr.includes('jemalloc') || result.stderr.includes('Unsupported system page size')) return true;
  // SIGABRT (134 = 128 + 6)
  if (result.exitCode === 134) return true;
  return false;
}

export async function runRipgrep(args: string[], opts?: { cwd?: string; env?: NodeJS.ProcessEnv }): Promise<ExecResult> {
  // If we've already determined the working path, use it
  if (rgPathChecked && resolvedRgPath) {
    return spawnRg(resolvedRgPath, args, opts);
  }

  // First attempt: bundled ripgrep
  if (!rgPathChecked) {
    const result = await spawnRg(rgPath, args, opts);
    
    if (!isBinaryFailure(result)) {
      // Bundled binary works, cache it
      resolvedRgPath = rgPath;
      rgPathChecked = true;
      return result;
    }

    // Bundled binary failed, try system rg
    const fallbackResult = await spawnRg('rg', args, opts);
    
    if (!isBinaryFailure(fallbackResult)) {
      // System rg works, cache it
      resolvedRgPath = 'rg';
      rgPathChecked = true;
      return fallbackResult;
    }

    // Neither works, mark as checked and return the original error
    rgPathChecked = true;
    return { 
      stdout: '', 
      stderr: 'Failed to spawn ripgrep. Neither bundled nor system rg is available.', 
      exitCode: -1 
    };
  }

  // rgPathChecked is true but resolvedRgPath is null - no working rg found
  return { 
    stdout: '', 
    stderr: 'Failed to spawn ripgrep. Neither bundled nor system rg is available.', 
    exitCode: -1 
  };
}


